import {
  CODE128_QUIET_ZONE_MODULES,
  code128Bars,
  modelTitle,
  shownModelNumber,
} from '@sitzmark/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Barcode } from '../../components/barcode.js';
import { useNotice } from '../../components/notice.js';
import { Button, Callout, Card, Loading, Mono } from '../../components/ui.js';
import { api, errorMessage } from '../../lib/api.js';
import { usePreference } from '../../lib/preferences.js';
import { useTypingFocus } from '../../lib/typing-focus.js';
import { readSheetEntry } from './count-entry.js';
import { gridOf, groupSheet, makerNumberOf } from './count-sheet-layout.js';
import type { ModelGroup, VendorGroup } from './count-sheet-layout.js';
import '../purchasing/receiving-document.css';

type CountView = Awaited<ReturnType<typeof api.counts.get.query>>;
type Sheet = Awaited<ReturnType<typeof api.counts.sheet.query>>;
type Row = Sheet['rows'][number];

/**
 * The count sheet (decision 211; client, 2026-10-15): "an inventory count
 * worksheet where people walk through store and count what inventory is on
 * hand … sku style name ordered by color then size … onhand count then a
 * space for them to fill out count then the sku and MFR number … they print
 * it off and work through it, anything that is off they come back to the
 * work sheet and update the count".
 *
 * On screen and on paper alike: vendor by vendor, then model by model (decision
 * 220), as a grid — colors down, sizes across — or as a list, its colors
 * together and sizes in size order; on hand as the books have it, and a box
 * for what is on the shelf.
 *
 * **Back at the register only what was off is typed** (client, 2026-10-16:
 * "you're really only going to go in and add numbers to the ones where count
 * is off … how can we make this process as intuitive, efficient and
 * reliable"). Every printed row carries its SKU as a barcode: scan the row on
 * the paper — or the item's own label, or type the SKU — and the sheet jumps
 * to it with its box ready; type the count, press Enter, and the caret is
 * back in the find box for the next. Then **Mark the rest as matching** counts
 * every box still blank as what the books say, so the count is complete and
 * posting changes only what was typed.
 */
export function CountSheet({
  sessionId,
  number,
  open,
  onChanged,
}: {
  sessionId: string;
  number: string;
  /** Counts can be typed only while the count is open. */
  open: boolean;
  onChanged: (view: CountView) => void;
}) {
  const queryClient = useQueryClient();
  const notify = useNotice();
  const key = ['counts', 'sheet', sessionId];
  const sheet = useQuery({ queryKey: key, queryFn: () => api.counts.sheet.query({ sessionId }) });
  const [printing, setPrinting] = useState(false);
  const [code, setCode] = useState('');
  const [found, setFound] = useState<string | null>(null);
  const [notOnSheet, setNotOnSheet] = useState<string | null>(null);
  const [confirmMatch, setConfirmMatch] = useState(false);
  const findRef = useRef<HTMLInputElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  // Grid first (D2), List a press away — remembered for the person (decision 220).
  const [view, setView] = usePreference('count-sheet-view', 'grid');
  // A scan with the caret anywhere on the sheet lands in its find box.
  const append = useCallback((character: string) => setCode((current) => current + character), []);
  useTypingFocus(findRef, append, open);

  const after = (view: CountView) => {
    onChanged(view);
    void queryClient.invalidateQueries({ queryKey: key });
  };
  const setCount = useMutation({
    mutationFn: (input: { variantId: string; countedQty: number }) =>
      api.counts.setCount.mutate({ sessionId, ...input }),
    onSuccess: after,
  });
  const clear = useMutation({
    mutationFn: (variantId: string) => api.counts.removeLine.mutate({ sessionId, variantId }),
    onSuccess: after,
  });
  const find = useMutation({
    mutationFn: (scanned: string) => api.counts.findOnSheet.query({ sessionId, code: scanned }),
    onSuccess: ({ variantId }, scanned) => {
      setCode('');
      // A list row or a grid cell: whichever is showing carries the size's id.
      const box = sheetRef.current?.querySelector<HTMLInputElement>(
        `[data-variant="${variantId}"] input`,
      );
      if (box === null || box === undefined) {
        setFound(null);
        setNotOnSheet(scanned);
        findRef.current?.focus();
        return;
      }
      setNotOnSheet(null);
      setFound(variantId);
      box.scrollIntoView({ block: 'center' });
      box.focus();
      box.select();
    },
    // A refused code stays in the box, selected, so the next beep replaces it.
    onError: () => {
      findRef.current?.focus();
      findRef.current?.select();
    },
  });
  const matchRest = useMutation({
    mutationFn: () => api.counts.matchRest.mutate({ sessionId }),
    onSuccess: (result) => {
      setConfirmMatch(false);
      after(result.view);
      notify({
        title: `Marked ${result.matched} size${result.matched === 1 ? '' : 's'} as matching`,
        ...(result.belowZero > 0
          ? {
              detail: `${result.belowZero} left blank: the books show less than zero on hand. Count those.`,
            }
          : {}),
      });
    },
  });

  if (sheet.isPending) return <Loading label="Loading the sheet" />;
  if (sheet.isError) return <Callout tone="danger">{errorMessage(sheet.error)}</Callout>;
  const { scope, rows } = sheet.data;
  const vendors = groupSheet(rows);
  const counted = rows.filter((row) => row.counted !== null).length;
  const blank = rows.length - counted;

  const backToFind = (scanned?: string) => {
    // A label scanned into a count box is a scan, not a count (count-entry.ts).
    if (scanned !== undefined) {
      find.mutate(scanned);
      return;
    }
    findRef.current?.focus();
  };
  const onCount = (variantId: string, countedQty: number | null) =>
    countedQty === null ? clear.mutate(variantId) : setCount.mutate({ variantId, countedQty });

  return (
    <Card
      title={`Sheet — ${scope.name}`}
      padded={false}
      className="counts__sheet-card"
      actions={
        <>
          <span className="counts__view" role="group" aria-label="Show the sheet as">
            <Button
              variant={view === 'grid' ? 'secondary' : 'ghost'}
              aria-pressed={view === 'grid'}
              onClick={() => setView('grid')}
            >
              Grid
            </Button>
            <Button
              variant={view === 'list' ? 'secondary' : 'ghost'}
              aria-pressed={view === 'list'}
              onClick={() => setView('list')}
            >
              List
            </Button>
          </span>
          {open ? (
            <Button
              variant="secondary"
              busy={matchRest.isPending}
              disabledReason={blank === 0 ? 'Every size has a count' : undefined}
              onClick={() => (confirmMatch ? matchRest.mutate() : setConfirmMatch(true))}
            >
              {confirmMatch
                ? `Really mark ${blank} blank as matching`
                : 'Mark the rest as matching'}
            </Button>
          ) : null}
          <Button variant="secondary" onClick={() => setPrinting(true)}>
            Print the sheet
          </Button>
        </>
      }
    >
      {open ? (
        <form
          className="counts__find"
          onSubmit={(event) => {
            event.preventDefault();
            if (code.trim() !== '') find.mutate(code.trim());
          }}
        >
          <label className="field__label" htmlFor="count-find">
            Find a size on the sheet
          </label>
          <input
            id="count-find"
            ref={findRef}
            className="search__input"
            placeholder="Scan the barcode on the printed row or the item, or type a SKU and press Enter"
            autoComplete="off"
            spellCheck={false}
            value={code}
            onChange={(event) => setCode(event.target.value)}
          />
        </form>
      ) : null}
      {find.isError ? <Callout tone="danger">{errorMessage(find.error)}</Callout> : null}
      {notOnSheet !== null ? (
        <Callout tone="warning">
          {notOnSheet} is not on this sheet — it is not filed under {scope.name}. Put it aside and
          look it up in Products.
        </Callout>
      ) : null}
      <p className="text-secondary counts__sheet-note" role="status">
        {counted} of {rows.length} sizes counted.{' '}
        {blank === 0
          ? 'Every size has a count.'
          : 'Type only what is off. A blank box changes nothing — or press Mark the rest as matching when you are done.'}
      </p>
      {setCount.isError ? <Callout tone="danger">{errorMessage(setCount.error)}</Callout> : null}
      {matchRest.isError ? <Callout tone="danger">{errorMessage(matchRest.error)}</Callout> : null}
      {rows.length === 0 ? (
        <p className="text-secondary counts__sheet-note">Nothing stocked is filed there.</p>
      ) : (
        <div className="counts__sheet-body" ref={sheetRef}>
          {vendors.map((vendor) => (
            <section key={vendor.key} className="counts__vendor" aria-label={vendor.vendorName}>
              <h3 className="counts__vendor-name">{vendor.vendorName}</h3>
              {view === 'grid' ? (
                vendor.models.map((model) => (
                  <SheetGrid
                    key={model.key}
                    model={model}
                    open={open}
                    found={found}
                    onEntered={backToFind}
                    onCount={onCount}
                  />
                ))
              ) : (
                <div className="table-scroll">
                  <table className="table counts__sheet">
                    <thead>
                      <tr>
                        <th>Color</th>
                        <th>Size</th>
                        <th>SKU</th>
                        <th>Mfr #</th>
                        <th className="num">On hand</th>
                        <th className="num">Counted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vendor.models.map((model) => (
                        <SheetStyle
                          key={model.key}
                          model={model}
                          open={open}
                          found={found}
                          onEntered={backToFind}
                          onCount={onCount}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          ))}
        </div>
      )}
      {printing ? (
        <PrintSheet
          number={number}
          scopeName={scope.name}
          vendors={vendors}
          view={view}
          onDone={() => setPrinting(false)}
        />
      ) : null}
    </Card>
  );
}

type Model = ModelGroup<Row>;

function SheetStyle({
  model,
  open,
  found,
  onEntered,
  onCount,
}: {
  model: Model;
  open: boolean;
  found: string | null;
  onEntered: (scanned?: string) => void;
  onCount: (variantId: string, countedQty: number | null) => void;
}) {
  return (
    <>
      <tr className="counts__sheet-style">
        <th colSpan={6} scope="rowgroup">
          <ModelName model={model} />
        </th>
      </tr>
      {model.rows.map((row, index) => {
        const sameColor = index > 0 && model.rows[index - 1]!.colorName === row.colorName;
        return (
          <tr
            key={row.variantId}
            data-variant={row.variantId}
            className={row.variantId === found ? 'counts__last' : undefined}
          >
            <td className={sameColor ? 'text-secondary' : undefined}>
              {sameColor ? '' : (row.colorName ?? '—')}
            </td>
            <td>{row.size ?? 'One size'}</td>
            <td>
              <Mono>{row.sku}</Mono>
            </td>
            <td>{row.mfr === null || row.mfr === '' ? '' : <Mono>{row.mfr}</Mono>}</td>
            <td className="num">{row.onHand}</td>
            <td className="num">
              {open ? (
                <SheetBox
                  label={boxLabel(model, row)}
                  value={row.counted}
                  onCommit={(value) => onCount(row.variantId, value)}
                  onEntered={onEntered}
                />
              ) : (
                (row.counted ?? '')
              )}
            </td>
          </tr>
        );
      })}
    </>
  );
}

function boxLabel(model: Model, row: Row): string {
  return `Counted ${model.styleName} ${row.colorName ?? ''} ${row.size ?? ''}`
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * One model as a grid: its colors down the side, its sizes across, each cell
 * the books' on hand with a box for what is on the shelf (decision 220). A
 * color and size the model does not come in is an empty cell.
 */
function SheetGrid({
  model,
  open,
  found,
  onEntered,
  onCount,
}: {
  model: Model;
  open: boolean;
  found: string | null;
  onEntered: (scanned?: string) => void;
  onCount: (variantId: string, countedQty: number | null) => void;
}) {
  const grid = gridOf(model);
  return (
    <div className="table-scroll counts__grid-wrap">
      <table className="table counts__grid">
        <caption className="counts__grid-name">
          <ModelName model={model} />
        </caption>
        <thead>
          <tr>
            <th scope="col">Color</th>
            {grid.sizes.map((size) => (
              <th key={size ?? ''} scope="col" className="num">
                {size ?? 'One size'}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {grid.colors.map((color) => (
            <tr key={color ?? ''}>
              <th scope="row">{color ?? '—'}</th>
              {grid.sizes.map((size) => {
                const row = grid.cell(color, size);
                if (row === undefined)
                  return <td key={size ?? ''} className="counts__grid-none" aria-hidden="true" />;
                return (
                  <td
                    key={size ?? ''}
                    data-variant={row.variantId}
                    className={`num${row.variantId === found ? ' counts__last' : ''}`}
                  >
                    <span className="counts__grid-onhand" title="On hand, as the books have it">
                      {row.onHand}
                    </span>
                    {open ? (
                      <SheetBox
                        label={boxLabel(model, row)}
                        value={row.counted}
                        onCommit={(value) => onCount(row.variantId, value)}
                        onEntered={onEntered}
                      />
                    ) : (
                      <span className="counts__grid-counted">{row.counted ?? ''}</span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * A box on the sheet: blank is "not counted", a number is the count. Kept on
 * blur or Enter — and Enter hands the caret back to the find box, ready for
 * the next scan.
 */
function SheetBox({
  label,
  value,
  onCommit,
  onEntered,
}: {
  label: string;
  value: number | null;
  onCommit: (value: number | null) => void;
  onEntered: (scanned?: string) => void;
}) {
  const shown = value === null ? '' : String(value);
  const [draft, setDraft] = useState(shown);
  useEffect(() => setDraft(shown), [shown]);
  // Set when Enter found a scan, so the blur that follows keeps nothing.
  const scannedRef = useRef(false);
  const commit = () => {
    const entry = readSheetEntry(draft, shown);
    if (entry.kind === 'clear') onCommit(null);
    else if (entry.kind === 'count') onCommit(entry.value);
    else setDraft(shown);
  };
  return (
    <input
      className="input counts__box"
      inputMode="numeric"
      aria-label={label}
      value={draft}
      onChange={(event) => setDraft(event.target.value.replace(/[^\d]/g, ''))}
      onBlur={() => {
        if (scannedRef.current) {
          scannedRef.current = false;
          return;
        }
        commit();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          const entry = readSheetEntry(draft, shown);
          if (entry.kind === 'scan') {
            // A label scanned into the box: find it, and keep the count as it was.
            scannedRef.current = true;
            setDraft(shown);
            event.currentTarget.blur();
            onEntered(entry.code);
            return;
          }
          // Blurring commits; the find box then takes the caret.
          event.currentTarget.blur();
          onEntered();
        }
      }}
    />
  );
}

const SHEET_PAGE = '@page { size: letter portrait; margin: 0.5in; }';
const GRID_PAGE = '@page { size: letter landscape; margin: 0.4in; }';

/**
 * A row's barcode, sized so every module prints at about 11 mil — the width a
 * hand scanner reads off plain paper — up to 1.4in, so a long SKU still fits
 * its column (narrower modules, still well within an imager's reach).
 */
function RowBarcode({ sku }: { sku: string }) {
  const modules = code128Bars(sku).width + 2 * CODE128_QUIET_ZONE_MODULES;
  return (
    <Barcode
      value={sku}
      height={30}
      className="counts__barcode"
      // The width is the code's own; a stretched barcode is an unreadable one.
      style={{ width: `${Math.min(1.4, modules * 0.011).toFixed(3)}in` }}
    />
  );
}

/**
 * The sheet on paper, as it is shown: vendor by vendor, then model.
 *
 * **Each model is its own table**, its name in the table's heading. A browser
 * repeats a table's heading at the top of every page the table runs onto, so a
 * model with sixty sizes that breaks across pages still says whose sizes they
 * are on the next page (client, 2026-10-16: "some items have a lot of variants
 * so they may run to the next page") — without printing the name on every row.
 * **List** gives each size a row with its SKU as a barcode, to scan the paper
 * back in; **Grid** gives each model its colors down and sizes across, with the
 * books' on hand and a box to write in — sideways, for the room.
 */
function PrintSheet({
  number,
  scopeName,
  vendors,
  view,
  onDone,
}: {
  number: string;
  scopeName: string;
  vendors: readonly VendorGroup<Row>[];
  view: 'grid' | 'list';
  onDone: () => void;
}) {
  /*
   * The page is on screen before the browser is asked to print it: an effect
   * runs after this component's portal is in the document. (Rendering it from
   * inside the effect printed a blank page — React will not flush there.)
   */
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = view === 'grid' ? GRID_PAGE : SHEET_PAGE;
    document.head.appendChild(style);
    window.print();
    style.remove();
    onDone();
    // Once, when opened.
  }, []);
  return createPortal(
    <div className="print-only">
      <article className="rodoc">
        <header className="rodoc__head">
          <div className="rodoc__title">
            <h1>Count sheet — {scopeName}</h1>
          </div>
        </header>
        <dl className="rodoc__meta">
          <div>
            <dt>Count</dt>
            <dd>{number}</dd>
          </div>
          <div>
            <dt>Printed</dt>
            <dd>{new Date().toLocaleString()}</dd>
          </div>
          <div>
            <dt>Counted by</dt>
            <dd>________________</dd>
          </div>
        </dl>
        <p className="counts__paper-note">
          {view === 'grid'
            ? 'Write a count only where the shelf differs from the number in the box. Back at the register, scan the item’s own label or type its SKU to jump to it.'
            : 'Write a count only where the shelf differs from On hand. Back at the register, scan a row’s barcode to jump to it.'}
        </p>
        {vendors.map((vendor) => (
          <section key={vendor.key} className="counts__paper-vendor">
            <h2 className="counts__paper-vendor-name">{vendor.vendorName}</h2>
            {vendor.models.map((model) =>
              view === 'grid' ? (
                <PaperGrid key={model.key} model={model} />
              ) : (
                <PaperList key={model.key} model={model} />
              ),
            )}
          </section>
        ))}
      </article>
    </div>,
    document.body,
  );
}

function PaperList({ model }: { model: Model }) {
  return (
    <table className="rodoc__lines counts__paper-style">
      {/* The same widths in every model's table, so the columns line up down the page. */}
      <colgroup>
        <col className="counts__col-color" />
        <col className="counts__col-size" />
        <col className="counts__col-sku" />
        <col className="counts__col-mfr" />
        <col className="counts__col-on-hand" />
        <col className="counts__col-counted" />
      </colgroup>
      <thead>
        <tr className="counts__paper-name">
          <th colSpan={6}>{modelTitle(model.styleNumber, model.styleName)}</th>
        </tr>
        <tr>
          <th>Color</th>
          <th>Size</th>
          <th>SKU</th>
          <th>Mfr #</th>
          <th className="num">On hand</th>
          <th className="num">Counted</th>
        </tr>
      </thead>
      <tbody>
        {model.rows.map((row, index) => {
          const sameColor = index > 0 && model.rows[index - 1]!.colorName === row.colorName;
          return (
            <tr key={row.variantId}>
              <td>{sameColor ? '' : (row.colorName ?? '—')}</td>
              <td>{row.size ?? 'One size'}</td>
              <td className="counts__paper-sku">
                <RowBarcode sku={row.sku} />
                <span className="code">{row.sku}</span>
              </td>
              <td>{row.mfr ?? ''}</td>
              <td className="num">{row.onHand}</td>
              <td className="counts__write-in" />
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function PaperGrid({ model }: { model: Model }) {
  const grid = gridOf(model);
  const mfr = makerNumberOf(model);
  return (
    <table className="rodoc__lines counts__paper-grid">
      <thead>
        <tr className="counts__paper-name">
          <th colSpan={grid.sizes.length + 1}>
            {modelTitle(model.styleNumber, model.styleName)}
            {mfr === null ? '' : ` · Mfr # ${mfr}`}
          </th>
        </tr>
        <tr>
          <th className="counts__paper-color">Color</th>
          {grid.sizes.map((size) => (
            <th key={size ?? ''} className="counts__paper-size">
              {size ?? 'One size'}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {grid.colors.map((color) => (
          <tr key={color ?? ''}>
            <td className="counts__paper-color">{color ?? '—'}</td>
            {grid.sizes.map((size) => {
              const row = grid.cell(color, size);
              return row === undefined ? (
                <td key={size ?? ''} className="counts__paper-none" />
              ) : (
                <td key={size ?? ''} className="counts__paper-cell">
                  <span className="counts__paper-onhand">{row.onHand}</span>
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** The model's number, when it is not just its name again, then its name (decision 222). */
function ModelName({ model }: { model: { styleNumber: string; styleName: string } }) {
  const number = shownModelNumber(model.styleNumber, model.styleName);
  return (
    <>
      {number === null ? null : (
        <>
          <Mono>{number}</Mono> ·{' '}
        </>
      )}
      {model.styleName}
    </>
  );
}
